/**
 * <load-more-products> — appends the next paginated page of cards to the grid.
 *
 * Progressive enhancement: the button is a real link to `paginate.next.url`, so the
 * collection still paginates with JS disabled. The element re-upgrades automatically
 * when facets.js replaces #ProductGridContainer after a filter change.
 */
class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('.cl-load-more__status');
    if (this.button) this.button.addEventListener('click', this.onClick.bind(this));
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get gridId() {
    return this.dataset.gridId || 'product-grid';
  }

  onClick(event) {
    event.preventDefault();
    if (this.loading || !this.button) return;

    const nextUrl = this.button.getAttribute('href');
    if (!nextUrl) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');

    fetch(this.buildUrl(nextUrl))
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => this.render(html))
      .catch(() => {
        // Fall back to a normal page load so the shopper is never stuck.
        window.location.href = nextUrl;
      })
      .finally(() => {
        this.loading = false;
        this.removeAttribute('aria-busy');
      });
  }

  buildUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  render(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = document.getElementById(this.gridId);
    const newGrid = parsed.getElementById(this.gridId);
    if (!grid || !newGrid) return;

    const items = newGrid.querySelectorAll(':scope > li');
    items.forEach((item) => grid.appendChild(item));

    const nextButton = parsed.querySelector('load-more-products [data-load-more-button]');
    if (nextButton && nextButton.getAttribute('href')) {
      this.button.setAttribute('href', nextButton.getAttribute('href'));
    } else {
      this.button.remove();
      this.button = null;
    }

    if (this.status) this.status.textContent = `${grid.querySelectorAll(':scope > li').length}`;
    if (items.length) items[0].querySelector('a, button')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
